/**
 * Authentication Type Definitions
 * 
 * KISS Principle: Simple, clear types that match backend DTOs
 * Following TypeScript best practices with clear naming
 */

import { ICheckoutProfile } from './checkout-profile.types';

// ============================================
// USER ENTITY
// ============================================

export interface IUser {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  checkoutProfile?: ICheckoutProfile | null;
  /** Custom `/store/:slug` when set (see seller Settings → Store). */
  storeSlug?: string | null;
  roles: UserRole[];
  /** Merged with role permissions client-side (must match server / JWT). */
  permissionOverrides?: Record<string, boolean> | null;
  isEmailVerified?: boolean;
  isActive: boolean;
  isGuest?: boolean;
  needsPasswordSetup?: boolean;
  twoFactorEnabled?: boolean;
  sellerOnboardingCompletedAt?: string | null;
  sellerProfile?: ISellerProfile | null;
  buyerNotificationPreferences?: IBuyerNotificationPreferences | null;
  uiPreferences?: IUiPreferences | null;
  createdAt: string;
  linkedAccounts?: {
    google: boolean;
    password: boolean;
  };
}

export type WorkspacePreferenceId =
  | 'buyer'
  | 'seller'
  | 'creator'
  | 'owner'
  | 'staff';

export interface IUiPreferences {
  lastSelectedWorkspace?: WorkspacePreferenceId | null;
}

export enum UserRole {
  OWNER = 'owner',
  SUPER_ADMIN = 'super_admin',
  DEPARTMENT_HEAD = 'department_head',
  ADMIN = 'admin',
  SPECIALIST = 'specialist',
  SUPPORT = 'support',
  SELLER = 'seller',
  /** Creator program role. */
  CREATOR = 'creator',
  REGULAR = 'regular',
}

// ============================================
// AUTH DTOs (Data Transfer Objects)
// ============================================

/**
 * Login Request Payload
 */
export interface ILoginDto {
  email: string;
  password: string;
  rememberMe?: boolean;
  /** Client-only: skip success toast when auth prompt handles messaging. */
  silent?: boolean;
}

/**
 * Register Request Payload
 */
export interface IRegisterDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
  roles?: UserRole[];
}

/**
 * Auth Response from Backend
 */
export interface IAuthResponse {
  access_token: string;
  refresh_token?: string;
  user: IUser;
  requiresTwoFactor?: boolean;
  twoFactorToken?: string;
  isNewUser?: boolean;
}

/**
 * Token Refresh Request
 */
export interface IRefreshTokenDto {
  refresh_token: string;
}

/**
 * Password Reset Request
 */
export interface IForgotPasswordDto {
  email: string;
}

export type AccountStatus =
  | 'active'
  | 'guest_pending_password'
  | 'oauth_only';

export interface ICheckEmailResult {
  exists: boolean;
  accountStatus?: AccountStatus;
  needsPasswordSetup?: boolean;
  message?: string;
}

export type ForgotPasswordAction = 'password_reset' | 'activation' | 'none';

export interface IForgotPasswordResponse {
  success: true;
  message: string;
  actionTaken: ForgotPasswordAction;
}

export interface IActivateAccountDto {
  token?: string;
  password: string;
}

/**
 * Password Reset Confirmation
 */
export interface IResetPasswordDto {
  token: string;
  password: string;
}

/**
 * Change Password (when logged in)
 */
export interface IChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

/**
 * Update User Profile DTO
 * Matches backend UpdateUserProfileDTO
 */
export interface IUpdateUserProfileDto {
  firstName?: string;
  lastName?: string;
  username?: string;
  /** Set a custom store URL, or `null` to clear and use username-only links. */
  storeSlug?: string | null;
  phone?: string | null;
  sellerProfile?: ISellerProfile;
  buyerNotificationPreferences?: IBuyerNotificationPreferences;
  uiPreferences?: IUiPreferences;
}

export type SellerBusinessType = 'individual' | 'business';

export interface ISellerNotificationPreferences {
  newOrdersEmail: boolean;
  payoutUpdatesEmail: boolean;
  weeklyDigestEmail: boolean;
  financeUpdatesEmail?: boolean;
  listingUpdatesEmail?: boolean;
}

export interface IBuyerNotificationPreferences {
  orderUpdatesEmail: boolean;
  returnUpdatesEmail: boolean;
  marketingEmail: boolean;
  promotionsEmail: boolean;
}

export interface ISellerProfile {
  storeDisplayName?: string | null;
  storeBio?: string | null;
  businessType?: SellerBusinessType | null;
  country?: string | null;
  marketingConsent?: boolean;
  termsAcceptedAt?: string | null;
  skippedSteps?: string[];
  notificationPreferences?: ISellerNotificationPreferences | null;
}

export interface ISellerOnboardingStatus {
  completed: boolean;
  completedAt: string | null;
  accountStatus?: string | null;
  payoutMethodCount: number;
  twoFactorEnabled: boolean;
  checklist: Record<string, { complete: boolean; skipped: boolean }>;
}

// ============================================
// AUTH STATE (Redux State Shape)
// ============================================

export interface IAuthState {
  // User info
  user: IUser | null;
  
  // Token (refresh token is in httpOnly cookie, not in state)
  accessToken: string | null;
  
  // Auth status
  isAuthenticated: boolean;
  
  // Loading states (for granular UI control)
  isLoading: boolean;
  isLoggingIn: boolean;
  isRegistering: boolean;
  isLoggingOut: boolean;
  
  // Error handling
  error: string | null;
  
  // Session management
  lastActivity: string | null;
  rememberMe: boolean;

  /** True after the first app auth bootstrap (refresh + /me) finishes. */
  bootstrapComplete: boolean;

  /** Shown on login after session expiry or auth failure (cleared on successful login). */
  sessionMessage: string | null;

  /** Pending 2FA challenge after password/OAuth step */
  twoFactorPending: boolean;
  twoFactorToken: string | null;
  twoFactorEmail: string | null;
}

// ============================================
// API ERROR TYPES
// ============================================

export interface IAuthError {
  message: string;
  statusCode: number;
  error?: string;
}

